import { action, computed, observable } from 'mobx';

import { BusyStore } from 'ui/base/button';

import { ResultKind } from 'services/base/types';

import { Host, HostMap, IPangolinDomain } from 'services/pangolin/types';
import { HostsService, HostsUpdateAllResult } from 'services/pangolin/hosts_service';

import { DeviceBandwidthStore } from './device_bandwidth/device_bandwidth_store';

type DeviceBandwidthPair = {
  host: Host;
  store: DeviceBandwidthStore;
};

export class BandwidthPrioritizationStore {
  @observable.shallow
  children: DeviceBandwidthPair[];

  @observable.ref
  busy: BusyStore;

  constructor(hosts: HostMap) {
    const children = hosts.map((host) => ({
      host, store: new DeviceBandwidthStore(
        host.uploadAllocation,
        host.downloadAllocation,
        computed(() => this.totalUpload),
        computed(() => this.totalDownload)
      ),
    }));

    children.sort((a, b) => {
      // sort them by up + down (decreasing),
      const aTotalUpDown = a.store.upload.value + a.store.download.value;
      const bTotalUpDown = b.store.upload.value + b.store.download.value;
      if (aTotalUpDown !== bTotalUpDown) {
        return bTotalUpDown - aTotalUpDown;
      }

      // then by name (lexicographic, case-insensitive).
      const aName = a.host.name.toUpperCase();
      const bName = b.host.name.toUpperCase();
      return aName.localeCompare(bName);
    });

    this.children = children;
    this.busy = new BusyStore();
  }

  @computed get totalUpload(): number {
    const uploads = this.children.map(({ store }) => store.upload.value);
    return uploads.reduce((sum, value) => sum + value, 0);
  }

  @computed get totalDownload(): number {
    const downloads = this.children.map(({ store }) => store.download.value);
    return downloads.reduce((sum, value) => sum + value, 0);
  }

  @action.bound
  async handleSave(domain: IPangolinDomain): Promise<HostsUpdateAllResult> {
    const hosts = this.children.map(({ host, store }) => ({
      id: host.id,
      uploadAllocation: store.upload.value,
      downloadAllocation: store.download.value,
    }));

    const valid = !hosts.find(host => !domain.hosts.has(host.id));
    if (!valid) return { kind: ResultKind.SYSTEM_ERROR };

    return await HostsService.updateAll(domain, hosts);
  }
}
